import { Component, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ScreenAnalyticsDirective } from '@flyfreely-portal-ui/analytics';
import {
    AuthorityFilter,
    DownloadService,
    FEATURE_MISSION_REPORTS,
    FlyFreelyError,
    FlyFreelyLoggingService,
    FormReportService,
    FormService,
    MissionReportService,
    toTimestamp,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import * as moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';

// Interface for the Mission Reports Form
interface MissionReportsForm {
    startDate: FormControl<string>;
    endDate: FormControl<string>;
}

// Interface for the Form Reports Form
interface FormReportsForm {
    startDate: FormControl<string>;
    endDate: FormControl<string>;
    formVersionId: FormControl<number>;
    exportFlights: FormControl<boolean>;
}

// Interface for the Flight Reports Form
interface FlightReportsForm {
    startDate: FormControl<string>;
    endDate: FormControl<string>;
    withFlightLogs: FormControl<boolean>;
}

@Component({
    selector: 'mission-reports-dialogue',
    templateUrl: './mission-reports-dialogue.component.html'
})
export class MissionReportsDialogue {
    @Input() organisationId: number;
    @Input() orOrganisationId: number;

    @Input() authorityIds: number[];
    @Input() canFilterByAuthority: boolean;
    @Input() missionAuthorityFilters: {
        name: string;
        children: AuthorityFilter[];
    }[];
    @Input() authorityFilter: AuthorityFilter;

    @ViewChild(ScreenAnalyticsDirective)
    private screenAnalytics: ScreenAnalyticsDirective;

    forms: {
        id: number;
        missionCount: number;
        formName: string;
        versionNumber: number;
        disabled: boolean;
    }[];

    missionReportsForm: FormGroup<MissionReportsForm>;
    formReportsForm: FormGroup<FormReportsForm>;
    flightReportsForm: FormGroup<FlightReportsForm>;

    missionReportsFeature = [FEATURE_MISSION_REPORTS];

    public working: boolean;
    private workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<MissionReportsDialogue>,
        private missionReportService: MissionReportService,
        private logging: FlyFreelyLoggingService,
        private formService: FormService,
        private formReportService: FormReportService,
        private downloadService: DownloadService
    ) {
        this.missionReportsForm = new FormGroup<MissionReportsForm>({
            startDate: new FormControl<string>(undefined, [
                Validators.required
            ]),
            endDate: new FormControl<string>(undefined, [])
        });
        this.formReportsForm = new FormGroup<FormReportsForm>({
            startDate: new FormControl<string>(undefined, [
                Validators.required
            ]),
            endDate: new FormControl<string>(undefined, [Validators.required]),
            formVersionId: new FormControl<number>(undefined, [
                Validators.required
            ]),
            exportFlights: new FormControl<boolean>(false, [])
        });
        this.flightReportsForm = new FormGroup<FlightReportsForm>({
            startDate: new FormControl<string>(undefined, [
                Validators.required
            ]),
            endDate: new FormControl<string>(undefined, [Validators.required]),
            withFlightLogs: new FormControl<boolean>(false, [
                Validators.required
            ])
        });
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.refreshForms();

        this.formService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshForms();
            });
    }

    refreshForms() {
        const { startDate, endDate, exportFlights } =
            this.formReportsForm.value;
        if (startDate == null || endDate == null || exportFlights == null) {
            this.forms = [];
            return;
        }

        this.formService
            .findForms(this.organisationId)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                mergeMap(forms =>
                    this.formReportService.findVersionUsageCount(
                        this.organisationId,
                        this.orOrganisationId,
                        forms.map(f => f.id),
                        this.authorityIds,
                        toTimestamp(moment(startDate).startOf('day').toDate()),
                        toTimestamp(moment(endDate).startOf('day').toDate()),
                        exportFlights
                    )
                )
            )
            .subscribe({
                next: versions => {
                    this.forms = versions.map(v => ({
                        id: v.formVersion.id,
                        formName: v.formVersion.formName,
                        versionNumber: v.formVersion.versionNumber,
                        missionCount: v.missionCount,
                        disabled: v.missionCount === 0
                    }));
                    this.forms.sort((a, b) => {
                        if (a.disabled !== b.disabled) {
                            return a.disabled ? 1 : -1;
                        }
                        return a.formName.localeCompare(b.formName);
                    });
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while loading form versions: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    runFormReport() {
        const doneWorking = this.workTracker.createTracker();

        this.screenAnalytics.track('form-report', 'download');

        const { startDate, endDate, formVersionId, exportFlights } =
            this.formReportsForm.value;

        this.formReportService
            .downloadFormReport(
                this.organisationId,
                this.orOrganisationId,
                this.authorityFilter.authorityIds,
                formVersionId,
                toTimestamp(moment(startDate).startOf('day').toDate()),
                toTimestamp(moment(endDate).endOf('day').toDate()),
                exportFlights
            )
            .then(
                () => this.hide(),
                error => {
                    this.logging.error(error);
                    doneWorking();
                }
            );
    }

    runMissionReport() {
        const doneWorking = this.workTracker.createTracker();

        this.screenAnalytics.track('mission-report', 'download');

        const { startDate, endDate } = this.missionReportsForm.value;

        const startTime = moment(startDate).startOf('day').toDate();
        const endTime =
            endDate != null ? moment(endDate).endOf('day').toDate() : null;

        this.downloadService
            .downloadFromUrl(
                this.missionReportService.getMissionListReportUrl(
                    this.organisationId,
                    this.orOrganisationId,
                    this.authorityFilter.authorityIds,
                    toTimestamp(startTime),
                    toTimestamp(endTime)
                )
            )
            .then(
                () => this.hide(),
                error => {
                    this.logging.error(error);
                    doneWorking();
                }
            );
    }

    runFlightReport() {
        const doneWorking = this.workTracker.createTracker();

        this.screenAnalytics.track('flight-report', 'download');

        const { startDate, endDate, withFlightLogs } =
            this.flightReportsForm.value;

        const startTime = moment(startDate).startOf('day').toDate();
        const endTime =
            endDate != null ? moment(endDate).endOf('day').toDate() : null;

        this.downloadService
            .downloadFromUrl(
                this.missionReportService.getFlightReportUrl(
                    this.organisationId,
                    this.orOrganisationId,
                    this.authorityFilter.authorityIds,
                    toTimestamp(startTime),
                    toTimestamp(endTime),
                    withFlightLogs
                )
            )
            .then(
                () => this.hide(),
                error => {
                    this.logging.error(error);
                    doneWorking();
                }
            );
    }

    updateMissionAuthorityFilter() {
        this.orOrganisationId = this.authorityFilter.orOrganisationId;
        this.authorityIds = this.authorityFilter.authorityIds;
        this.organisationId = this.authorityFilter.organisationId;
    }

    hide() {
        this.modal.hide();
    }

    formsSearch = (term: string, { formName }: { formName: string }) =>
        formName.toLocaleUpperCase().includes(term.toLocaleUpperCase());
}
